import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import styles from './TextEditor.module.css';

import { $getRoot, $createParagraphNode, $createTextNode } from 'lexical';

import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';

import { HeadingNode, QuoteNode } from '@lexical/rich-text';
import { TableCellNode, TableNode, TableRowNode } from '@lexical/table';
import { ListItemNode, ListNode } from '@lexical/list';
import { CodeHighlightNode, CodeNode } from '@lexical/code';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { ImageNode } from './plugins/ImagePlugin/ImageNodes';

import { $generateHtmlFromNodes } from '@lexical/html';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useLexicalIsTextContentEmpty } from '@lexical/react/useLexicalIsTextContentEmpty';
import LexicalErrorBoundary from '@lexical/react/LexicalErrorBoundary';

import ToolbarPlugin from './plugins/ToolbarPlugin/ToolbarPlugin';
import CodeHighlightPlugin from './plugins/CodeHighlightPlugin/CodeHighlightPlugin';
import AutoLinkPlugin from './plugins/AutoLinkPlugin/AutoLinkPlugin';

import exampleTheme from './lexicalThemeConfig';

const initialState = () => {
  const paragraph = $createParagraphNode();
  const text = $createTextNode();
  paragraph.append(text);
  const root = $getRoot();
  root.append(paragraph);
  root.selectEnd();
};

const StyledHtmlWrapper = (content) => {
  return `<div style="width: min(100%, 34.5em); margin-top: 3.55em; margin-bottom: 6.1em; padding-left: 2em; padding-right: 2em; margin-inline: auto;">${content}</div>`;
};

const TextEditor = ({ show, onHide, onSiteAction, setDraggedFile }) => {
  const [editorText, setEditorText] = useState('');
  const [isTextEditorEmpty, setIsTextEditorEmpty] = useState(true);

  const OnChangePlugin = ({ onChange }) => {
    const [editor] = useLexicalComposerContext();

    useEffect(() => {
      return editor.registerUpdateListener(({ editorState }) => {
        onChange(editorState, editor);
      });
    }, [onChange, editor]);
    return null;
  };

  const onClose = () => {
    setEditorText('');
    onHide();
  };

  const onError = (error) => {
    console.error(error);
  };

  const handleChange = (editorState, editor) => {
    if (editorState && editor) {
      editor.update(() => {
        const htmlString = $generateHtmlFromNodes(editor, null);
        setEditorText(htmlString);
      });
    }
  };

  const IsEditorEmpty = () => {
    const [editor] = useLexicalComposerContext();
    const isEmpty = useLexicalIsTextContentEmpty(editor);

    useEffect(() => {
      if (editor) {
        // Check if the editor content is empty
        setIsTextEditorEmpty(isEmpty);
      }
    }, [isEmpty, editor]);

    return null;
  };

  const onSave = () => {
    if (!isTextEditorEmpty) {
      const text2html = new File([StyledHtmlWrapper(editorText)], 'rich_text.html', {
        type: 'text/html',
        lastModified: new Date(),
        path: 'rich_text.html'
      });
      setDraggedFile(text2html);
      onSiteAction();
    }
    onClose();
  };

  const initialConfig = {
    theme: exampleTheme,
    onError,
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      CodeNode,
      CodeHighlightNode,
      TableNode,
      TableCellNode,
      TableRowNode,
      AutoLinkNode,
      LinkNode,
      ImageNode
    ],
    editorState: initialState
  };

  return (
    <Modal
      aria-labelledby="contained-modal-title-vcenter"
      backdrop="static"
      className="height-95"
      show={show}
      size="xl"
      centered
      onHide={onClose}
    >
      <div className={styles.container} id="modal-editor">
        <div className={styles.header}>
          <h2 className={styles.headerText}>Text Editor</h2>
          <div className={styles.btnContainer}>
            <Button variant='outline-light' onClick={onClose}>
              {isTextEditorEmpty ? 'Close' : 'Discard'}
            </Button>
            {!isTextEditorEmpty && (
              <Button className={styles.button} variant="purple" onClick={onSave} disabled={isTextEditorEmpty}>
                Continue
              </Button>
            )}
          </div>
        </div>
        <div className={styles.textEditor}>
          <LexicalComposer initialConfig={initialConfig}>
            <div className={styles.container}>
              <ToolbarPlugin />
              <div className={styles.editorContainer}>
                <RichTextPlugin
                  contentEditable={<ContentEditable className={styles.editorView} />}
                  // placeholder={<div className={styles.editorPlaceHolder} >enter some text...</div>}
                  placeholder={null}
                  ErrorBoundary={LexicalErrorBoundary}
                />
                <HistoryPlugin />
                <OnChangePlugin onChange={handleChange} />
                <CodeHighlightPlugin />
                <ListPlugin />
                <AutoLinkPlugin />
                <LinkPlugin />
                <IsEditorEmpty />
              </div>
            </div>
          </LexicalComposer>
        </div>
      </div>
    </Modal>
  );
};

export default TextEditor;
